import {
  ArrowUpTrayIcon,
  CheckBadgeIcon,
  danger,
  ErrorMessage,
  fileToDataUrl,
  ImageLargePreview,
  MagnifyingGlassPlusIcon,
  MAX_IMAGE_SIZE,
  useEffect,
  useRef,
  useState,
} from "@";

const UploadImageCard = (props) => {
  const fileInputRef = useRef(null);
  const [imageUrl, setImageUrl] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const [imagePreviewUrl, setImagePreviewUrl] = useState("");

  const handleImageChange = async (file) => {
    if (file.size > MAX_IMAGE_SIZE) {
      danger(`أقصى حجم مسموح به للصورة ${MAX_IMAGE_SIZE / 1024 / 1024} MB`);
      props.upload("");
      return;
    }

    if (file.type.slice(0, 5) !== "image") {
      danger("يرجى تحميل ملف الصورة");
      props.upload("");
      return;
    }

    const imageAsBase64 = await fileToDataUrl(file);
    setImageUrl(imageAsBase64);
    props.upload(file, imageAsBase64);
  };

  const handleFileInputChange = (event) => {
    const file = event.target.files[0];
    if (file) handleImageChange(file);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
    const file = event.dataTransfer.files[0];
    if (file) handleImageChange(file);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (props.success) {
      setImageUrl("");
    }
  }, [props.success]);

  const hasImage = !!(imageUrl || props.src);
  const isUploaded = !!imageUrl;

  return (
    <>
      <ImageLargePreview
        url={imagePreviewUrl}
        image={imagePreviewUrl}
        hide={() => setImagePreviewUrl("")}
      />

      <div
        className={`mb-5 rounded-2xl transition-colors ${
          hasImage
            ? "h-fit border border-gray-100 bg-gradient-to-br from-white to-gray-50/50 p-2 dark:border-gray-600 dark:from-gray-700 dark:to-gray-800/40"
            : ""
        }`}
      >
        {props.description && (
          <label className="input-label">
            {props.description}
            {props.star && <span className="star">*</span>}
          </label>
        )}
        <div className="flex flex-wrap items-center gap-2 md:gap-3">
          {props.show && (
            <label
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={`group relative mx-auto flex w-full cursor-pointer flex-col justify-center overflow-hidden rounded-2xl border-2 border-dashed bg-gradient-to-br p-3 text-center transition-all duration-300 hover:-translate-y-px md:mx-0 ${
                props.className
              } ${
                isDragging
                  ? "scale-[1.01] border-purple-500 from-purple-100/60 via-white to-purple-50 shadow-md shadow-purple-900/10 dark:border-purple-400 dark:from-purple-950/60 dark:via-gray-800 dark:to-purple-950/30"
                  : isUploaded
                    ? "border-emerald-300 from-emerald-50/40 via-white to-emerald-50/20 hover:border-emerald-400 dark:border-emerald-700 dark:from-emerald-950/40 dark:via-gray-800 dark:to-emerald-950/20"
                    : "border-gray-200 from-gray-50/60 via-white to-purple-50/20 hover:border-purple-400 hover:from-purple-50/40 hover:via-white hover:to-purple-50/40 dark:border-gray-600 dark:from-gray-800 dark:via-gray-800 dark:to-purple-950/20 dark:hover:border-purple-500"
              }`}
            >
              {/* Decorative orb */}
              <span
                aria-hidden
                className={`pointer-events-none absolute -end-10 -top-10 h-28 w-28 rounded-full blur-2xl transition-opacity duration-300 ${
                  isDragging
                    ? "bg-gradient-to-br from-purple-300/50 to-transparent opacity-100"
                    : isUploaded
                      ? "bg-gradient-to-br from-emerald-200/40 to-transparent opacity-80"
                      : "bg-gradient-to-br from-purple-200/30 to-transparent opacity-60 dark:from-purple-800/20"
                }`}
              />

              <div className="relative flex flex-wrap items-center justify-center gap-2 dark:text-gray-400">
                <span
                  className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg p-2 ring-1 transition-all duration-300 ${
                    isUploaded
                      ? "bg-gradient-to-br from-emerald-500 to-emerald-700 text-white shadow-md shadow-emerald-700/30 ring-emerald-600/20"
                      : isDragging
                        ? "bg-gradient-to-br from-purple-800 to-purple-950 text-white shadow-md shadow-purple-900/30 ring-purple-600/20"
                        : "bg-white text-purple-700 ring-purple-100 group-hover:bg-purple-100 group-hover:text-purple-900 group-hover:ring-purple-200 dark:bg-gray-900 dark:text-purple-300 dark:ring-purple-800/40"
                  }`}
                >
                  {isUploaded ? (
                    <CheckBadgeIcon className="h-5 w-5" />
                  ) : (
                    <ArrowUpTrayIcon
                      className={`h-4.5 w-4.5 transition-transform duration-300 ${
                        isDragging
                          ? "translate-y-0"
                          : "group-hover:-translate-y-0.5"
                      }`}
                    />
                  )}
                </span>

                <p className="text-[15px] font-medium tracking-tight text-gray-700 dark:text-gray-300">
                  {isDragging ? "أفلت الصورة هنا" : props.title || "اختر صورة"}
                  {props.star && <small className="star">*</small>}
                </p>
              </div>

              <input
                hidden
                type="file"
                id="fileInput"
                ref={fileInputRef}
                onChange={handleFileInputChange}
                encType="multipart/form-data"
                accept={props.accept || ".png, .jpg, .jpeg"}
              />
            </label>
          )}

          {hasImage && (
            <div
              className="group/preview relative flex items-center justify-center overflow-hidden rounded-2xl ring-1 ring-gray-100 dark:ring-gray-700"
              style={{ width: 250, height: 70 }}
            >
              {/* Hover gradient overlay */}
              <span
                aria-hidden
                className="pointer-events-none absolute inset-0 z-10 bg-gradient-to-t from-black/60 via-black/20 to-transparent opacity-0 transition-opacity duration-300 group-hover/preview:opacity-100"
              />

              <button
                type="button"
                onClick={() => setImagePreviewUrl(imageUrl || props.src)}
                className="absolute end-1/2 top-1/2 z-20 flex h-10 w-10 -translate-y-1/2 translate-x-1/2 cursor-zoom-in items-center justify-center rounded-full bg-white/95 text-purple-900 opacity-0 shadow-lg shadow-black/20 ring-1 ring-white/40 backdrop-blur-md transition-all duration-300 hover:scale-110 group-hover/preview:opacity-100 dark:bg-gray-900/95 dark:text-purple-300"
                aria-label="معاينة الصورة"
              >
                <MagnifyingGlassPlusIcon className="h-5 w-5" />
              </button>

              <img
                alt="image preview"
                loading="lazy"
                className="h-full w-full rounded-2xl object-cover transition-transform duration-500 group-hover/preview:scale-110"
                width={250}
                height={70}
                src={imageUrl || props.src}
              />
            </div>
          )}
        </div>

        {props.children}

        {props.error && <ErrorMessage value={props.error} />}
      </div>
    </>
  );
};

export default UploadImageCard;
